// Baked in at build time via rollup-plugin-replace (see rollup.config.mjs) — one
// value per environment (qa/stage/prod), plus a LOCAL_WEBCHAT_ORIGIN short-circuit
// for local dev. The bundle itself never varies by tenant; only this origin
// varies by *build* environment. The loader makes no network call of its own —
// it builds the iframe src directly from this origin plus tenantId/agentId (see
// the Web Chat Widget — UI Hosting Options doc's "Sequence of events" section).
export const WEBCHAT_UI_ORIGIN = process.env.WEBCHAT_UI_ORIGIN as string;

// Same rollup replace pass — package.json version + BUILD env name, surfaced in
// diagnostics dumps so support/agents can tell which loader landed on the page.
export const WEBCHAT_EMBED_VERSION = process.env.WEBCHAT_EMBED_VERSION as string;
export const WEBCHAT_EMBED_ENV = process.env.WEBCHAT_EMBED_ENV as string;

export const SCRIPT_ATTR = {
  tenantId: 'data-tenant-id',
  agentId: 'data-agent-id',
  // Escape hatch mirroring scheduler-embed's attachWidgetIframeToClientWebsite.ts:
  // lets a tenant mount the iframe inside their own element instead of the
  // default hidden container.
  staticContainerId: 'data-static-container-id',
  // SETO-7267: embedder-set source-tracking fields, mirroring scheduler-embed's
  // data-affiliate/data-campaign (see sourceTracking.ts). affiliate has no URL equivalent, so
  // there's nothing for it to conflict with. campaign does have one (?campaign=, added later) --
  // that URL param takes priority over this attribute, which now serves as its fallback.
  affiliate: 'data-affiliate',
  campaign: 'data-campaign',
  // SETO-7698: opens the widget automatically once 'ready' fires -- independent of
  // floatingButton (SETO-7735): both can be set together, the widget just opens immediately and
  // the button stays hidden until the visitor closes it (see init.ts). Tolerant-boolean: any
  // value other than the literal "true" is dropped/treated as absent rather than throwing.
  autoOpen: 'data-auto-open',
  // SETO-7699: renders a floating launcher button instead of relying on a tenant-authored
  // trigger. Same tolerant-boolean parsing as autoOpen above. Renamed from data-auto-open-button
  // (SETO-7735) -- the button's presence has nothing to do with data-auto-open, and the old name
  // read as if it were a variant of that attribute rather than its own independent feature.
  floatingButton: 'data-floating-button',
  // Deprecated pre-SETO-7735 name for floatingButton above -- kept as a synonym so tenant sites
  // that shipped the old attribute don't silently lose the button on their next embed-script
  // fetch. peekScriptInstallHints (diagnostics.ts) reads it straight off script.dataset as
  // `autoOpenButton` rather than through this constant; floatingButton wins when both are present.
  // Not surfaced in docs/api.md -- new installs should only ever see the new name.
  legacyFloatingButton: 'data-auto-open-button',
  // SETO-7699: which side of the viewport the widget anchors to (always along the bottom) --
  // covers both the floating launcher button (when floatingButton is set) and the chat window
  // itself (iframe/fallback), so the two always float together. See WEBCHAT_POSITIONS below for
  // the accepted values and fallback behavior.
  webchatPosition: 'data-webchat-position',
  // SETO-7699 (icon-bubble addendum): which visual shape the launcher renders as -- see
  // LAUNCHER_BUTTON_SHAPES below. Renamed from data-auto-open-button-shape alongside
  // data-floating-button above (SETO-7735), for the same reason.
  floatingButtonShape: 'data-floating-button-shape',
  // Deprecated pre-SETO-7735 name for floatingButtonShape above -- same synonym treatment as
  // legacyFloatingButton.
  legacyFloatingButtonShape: 'data-auto-open-button-shape',
} as const;

// SETO-7699: data-webchat-position's accepted values -- shared by the launcher button and the
// chat window itself (iframe/fallback) so the whole widget anchors to one consistent side.
// Always along the bottom of the viewport -- no top placement. Anything else (missing,
// misspelled, etc.) falls back to DEFAULT_WEBCHAT_POSITION below rather than throwing.
// WebchatPosition (types.ts) is derived from this array so the two can't drift apart.
export const WEBCHAT_POSITIONS = ['right', 'left'] as const;

// review: AMattRiley -- a standalone literal, not WEBCHAT_POSITIONS[0], so reordering the array
// above (e.g. to change validation-error messages, or just alphabetizing) can't silently change
// the shipped default as a side effect. Still type-checked against the array via the annotation.
export const DEFAULT_WEBCHAT_POSITION: (typeof WEBCHAT_POSITIONS)[number] = 'right';

// SETO-7699 (icon-bubble addendum): data-floating-button-shape's accepted values -- 'label' is
// the original "Chat with us" pill, 'icon' is the icon-only minimized-chat bubble. Anything else
// falls back to DEFAULT_LAUNCHER_BUTTON_SHAPE below, same tolerant-validation posture as the
// position above.
export const LAUNCHER_BUTTON_SHAPES = ['label', 'icon'] as const;

// review: AMattRiley -- standalone literal, same reasoning as DEFAULT_WEBCHAT_POSITION above.
export const DEFAULT_LAUNCHER_BUTTON_SHAPE: (typeof LAUNCHER_BUTTON_SHAPES)[number] = 'label';

export const LAUNCHER_BUTTON_ELEMENT_ID = 'st-webchat-launcher-button';

// Fixed copy (SETO-7699), not configurable via a data attribute -- same precedent as
// FALLBACK_MESSAGE_TEXT below.
export const LAUNCHER_BUTTON_LABEL_TEXT = 'Chat with us';

// review: AMattRiley -- icon shape's sizing, centralized here rather than inline in
// launcherButton.ts's style strings/SVG attributes, matching this file's existing convention for
// floating-widget sizing (IFRAME_OPEN_WIDTH_PX etc. below). Placeholder pending an actual design
// spec, same caveat as those.
//
// 56px is a common minimized-chat-bubble diameter (Intercom/Drift-class widgets); comfortably
// exceeds the 44x44 CSS touch-target minimum with no extra hit-area work needed.
export const LAUNCHER_BUTTON_ICON_SIZE_PX = 56;

// Plain speech-bubble silhouette (rounded rect + a small pointed tail) rendered inside the
// icon-shape button above -- grouped as one object rather than loose constants since these values
// only mean anything relative to each other (the tail path is drawn against this exact viewBox).
export const CHAT_BUBBLE_ICON_GLYPH_SIZE_PX = 28;
export const CHAT_BUBBLE_ICON = {
  viewBox: '0 0 24 24',
  body: { x: 3, y: 4, width: 18, height: 13, rx: 4 },
  tailPath: 'M8 17 L8 21.5 L12.5 17 Z',
} as const;

export const SCRIPT_ELEMENT_ID = 'st-webchat-embed';

export const IFRAME_ELEMENT_ID = 'st-webchat-iframe';

export const CONTAINER_ELEMENT_ID = 'st-webchat-container';

// SETO-7774: full-layout-viewport sheet under the open mobile surface (iframe or fallback).
export const MOBILE_BACKDROP_ELEMENT_ID = 'st-webchat-mobile-backdrop';

// Delegated click-trigger class, mirroring scheduler-embed's
// se-ai-chat-show/st-ai-chat-show pattern: any element on the host page with
// this class opens the chat when clicked, no direct API call needed.
export const TRIGGER_CLASS = 'st-webchat-show';

// Floating overlay default size (open() sets these; close() collapses back to
// 0x0 — see iframe.ts). Placeholder values pending an actual design spec —
// width is fixed at the default size; height is adjustable by the child via the
// webchat:resize message, clamped to the min/max below. webchat:expand-request
// swaps in the expanded pair (wider + taller) so more of the transcript fits --
// but only on a viewport that can actually hold it (see expandedBoxFits in
// iframe.ts); note IFRAME_EXPANDED_WIDTH_PX and MOBILE_BREAKPOINT_PX are
// coincidentally equal, so the narrowest desktop-classified viewports cannot.
export const IFRAME_OPEN_WIDTH_PX = 380;
export const IFRAME_OPEN_HEIGHT_PX = 600;
export const IFRAME_EXPANDED_WIDTH_PX = 480;
export const IFRAME_EXPANDED_HEIGHT_PX = 720;
export const IFRAME_MIN_HEIGHT_PX = 200;
export const IFRAME_MAX_HEIGHT_PX = 800;

// SETO-7661: how long the desktop width/height change animates (iframe.ts builds the CSS
// transition from this), and how long init.ts keeps ignoring inbound webchat:resize heights
// afterwards. The settle margin covers the gap between the transition's nominal end and the
// browser actually landing on the final size -- an intermediate content height applied inside
// that window fights the animation and flickers. Both live here so the two durations can never
// drift apart: a transition longer than the suppression window reopens exactly that race.
export const DESKTOP_SIZE_TRANSITION_MS = 240;
export const SIZE_TRANSITION_SETTLE_MS = 40;

// SETO-7278: gap between the floating box and the bottom + left/right viewport edges it's
// anchored to (data-webchat-position, SETO-7699) on desktop — was flush (0px) before, which read
// as a layout bug more than a deliberate design choice. Placeholder value, same "pending real
// design input" caveat as the sizes above.
export const IFRAME_OFFSET_PX = 16;

// SETO-7278: below this viewport width, the widget goes full-screen (100vw/100vh,
// no offset) instead of the fixed floating box — the same box at a phone's width
// either overflows or reads as disproportionately tiny. Doesn't reopen SETO-7080's
// "no full-page-lock modal mode" decision, which was about desktop; this is the
// standard behavior for this class of widget at mobile widths (Intercom, Drift,
// etc. all do the same). 480px is a common small-phone cutoff, not derived from a
// design spec — revisit if one materializes.
export const MOBILE_BREAKPOINT_PX = 480;

// How long init() waits for the iframe to post webchat:ready before treating
// the load as failed (web-chat-ui down, blocked by the tenant's own CSP,
// wrong origin, etc.) and showing the fallback below instead. Placeholder
// value pending real-world data on worst-case cold-start latency.
export const READY_TIMEOUT_MS = 8000;

export const FALLBACK_ELEMENT_ID = 'st-webchat-fallback';

// Shown in place of the iframe once READY_TIMEOUT_MS elapses with no
// webchat:ready — see createFallback() in iframe.ts. The loader can't render
// anything *inside* the iframe itself (it's a separate origin/document), so
// this is a sibling element the loader swaps in instead.
export const FALLBACK_MESSAGE_TEXT = 'Chat is temporarily unavailable. Please try again later.';

// Same accessible name as web-chat-ui's ChatHeader / OriginMismatchPanel close
// control — the fallback is a host-page sibling (the loader cannot write into
// the cross-origin iframe), so it has to carry its own X. Escape already
// closes this surface; the button is the visible equivalent.
export const FALLBACK_CLOSE_LABEL = 'Close chat';
